import qs, { type ParseOptions } from "query-string";
import { useCallback, useContext } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router";

import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { except } from "utilities/array/array";
import { routes } from "utilities/routes";

/**
 * function to use with showInNewTab prop.
 * showInNewTab prop is going to assume you are opening a /app specific page.
 * Some of the urls passed in to links pass the routes.appBase already by either directly appending it
 * or by the getBaseRoute() method. We have difference on how that approach works based on a
 * combination of in iframe usage, in SPA usage, and in combination with Link component usage.
 * The newly added prop leverages this function to get it right regardless  of the other combination.
 * Please contact Architecture if you find you need to adjust this for additional use case.
 * TODO: (SPA cleanup) Re-evaluate usage of function when fully SPA.
 */
export const formatRouteForNewTab = (to: string) => {
    if (
        to?.toString().toLowerCase().startsWith("/") &&
        !to?.toString().toLowerCase().startsWith("/app")
    ) {
        return `${routes.appBase}${to}`;
    }
    return to;
};

/**
 * Detects if the passed domain is http://localhost https://localhost ony any port
 * @param domain make sure the domain is the full domain name
 */
export function isLocalhostDomain(domain: string) {
    const localhostDomainRegex = /(https?:\/\/localhost(?::\d+)?)/;
    const isLocalhost = localhostDomainRegex.test(domain);

    return isLocalhost;
}

/**
 * Detects if the passed domain is *.buildertrend.net
 * @param domain make sure the domain is the full domain name
 */
export function isBuildertrendDomain(domain: string) {
    return domain.endsWith("buildertrend.net");
}

/**
 * Detects if the passed domain is *.buildertrendsystems.com
 * @param domain make sure the domain is the full domain name
 */
export function isPaymentProcessingServiceDomain(domain: string) {
    return domain.endsWith("buildertrendsystems.com") || domain.endsWith("ngrok.io");
}

export function getOAuthUrl(redirectUri: string, oAuthCode: string, state: string) {
    const parsed = qs.parseUrl(redirectUri);
    const result = qs.stringifyUrl({
        url: parsed.url,
        query: {
            ...parsed.query,
            // merge in the code and state with any existing qs params
            code: oAuthCode,
            state,
        },
    });
    return result;
}

export const removeQueryStringParams = (queryString: string, excludedKeys: string[] = []) => {
    const qsParams = new URLSearchParams(queryString);
    const deleteParams: string[] = [];
    const exceptions = ["filter", ...excludedKeys];

    qsParams.forEach((val, key) => deleteParams.push(key));
    except(deleteParams, exceptions).forEach((val) => qsParams.delete(val));

    return qsParams.toString();
};

/**
 * @returns Boolean indicating if we are inside an iframe
 */
export const isInIframe = (): boolean => {
    return window.location !== window.parent.location;
};

/**
 * This hook automatically uses location to grab the query string,
 * and then parses it into an object.
 * @param options Options on how the parsing is performed
 * @returns Parsed query string params
 */
export function useQueryString<T>(options?: ParseOptions) {
    const { search } = useLocation();
    return parseParams<T>(search, options);
}

export function useRouteProps() {
    const location = useLocation();
    const match = useRouteMatch();
    const history = useHistory();
    const spaInfoContext = useContext(SpaInfoContext);
    const isSpa = spaInfoContext?.isSpa ?? false;

    const removeQueryStringParamsReactRouter = useCallback(
        (...keys: string[]) => {
            if (history.location.pathname !== location.pathname) {
                // Do nothing, route change wasn't commited yet.
                return;
            }

            if (!isSpa) {
                removeQueryStringParamsFromUrl(...keys);
                return;
            }
            const url = new URLSearchParams(location.search);
            keys.forEach((key) => url.delete(key));
            const qsp = url.toString();
            const search = qsp ? `?${qsp}` : "";
            history.replace(location.pathname + search, location.state);
        },
        [history, location.state, location.pathname, location.search, isSpa],
    );

    return { location, match, history, removeQueryStringParamsReactRouter };
}

export function parseParams<T>(query: string, options?: ParseOptions) {
    return qs.parse(query, options) as Partial<T>;
}

/**
 * Clears query string parameters from the URL by adding a new history entry.
 * This creates a new browser history entry, so users can navigate back to the URL with query params.
 * Use this when you want the previous URL (with query params) to be accessible via back button.
 */
export const clearQueryStringWithHistory = () => {
    if (window.location.search.length > 0) {
        const url = window.location.href.split("?")[0];

        window.history.pushState(null, "", url);
    }
};

/**
 * Clears query string parameters from the URL by replacing the current history entry.
 * This overwrites the current history entry, so users cannot navigate back to the URL with query params.
 * Use this when you want to remove query params without creating navigable history.
 */
export const clearQueryString = () => {
    if (window.location.search.length > 0) {
        const url = window.location.href.split("?")[0];

        window.history.replaceState(null, "", url);
    }
};

export const removeQueryStringParamsFromUrl = (...keys: string[]) => {
    if (isInIframe() && window.top) {
        const url = new URL(window.top.location.href);
        keys.forEach((key) => url.searchParams.delete(key));
        window.top.history.pushState(null, "", url.toString());
    }
    const url = new URL(window.location.href);
    keys.forEach((key) => url.searchParams.delete(key));
    window.history.pushState(null, "", url.toString());
};
